import type { CatServiceAPIProductOption } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import type { CatServiceAPIResponseBody } from 'src/services/cat-service-api/types/CatServiceAPIResponseBody';
import type { ProductState } from '../types';
import { extractRelatedCategories } from './extractRelatedCategories';
import { partnerLocationIsValid } from './partnerLocationIsValid';

/** Normalise option payload from cat-service (camelCase + snake_case min/max images). */
export const normalizeProductOption = (
  raw: CatServiceAPIProductOption & Record<string, unknown>,
): CatServiceAPIProductOption => {
  const minImages =
    raw.minImages ?? (typeof raw.min_images === 'number' ? raw.min_images : null) ?? null;
  const maxImages =
    raw.maxImages ?? (typeof raw.max_images === 'number' ? raw.max_images : null) ?? null;

  return {
    ...raw,
    minImages,
    maxImages,
  };
};

// This should not adapt the whole product slice of the store, only the parts coming from cat-service-api
export const adaptCatServiceAPIResponseForStore = (
  catServiceAPIResponseBody: CatServiceAPIResponseBody,
): Omit<ProductState, 'attributes' | 'publicAttributes'> & {
  options: CatServiceAPIProductOption[];
} => {
  const { metaDescription, metaRobotsIndexable, navigation, product } = catServiceAPIResponseBody;
  const { partner } = product;
  const { deliveryOptions, options, returnsInformation, ...restOfProduct } = product;
  const gbpPrice = product.prices.find(({ currency }) => currency === 'GBP')?.amount;
  const exchangeRate = gbpPrice ? product.price.amount / gbpPrice : 1;

  const relatedCategories = extractRelatedCategories(navigation);

  return {
    error: null,
    ...{
      ...restOfProduct,
      options: options.map((o) => {
        const option = normalizeProductOption(
          o as CatServiceAPIProductOption & Record<string, unknown>,
        );

        return {
          ...option,
          // Store in minor units (matches price.amount / priceModifier.prices), converted to the selected currency.
          price: option.price == null ? null : Math.round(option.price * exchangeRate * 100),
        };
      }),
      partner: {
        ...product.partner,
        location: partnerLocationIsValid(partner.location) ? partner.location : '',
      },
    },
    relatedCategories,
    metaDescription,
    metaRobotsIndexable,
  };
};
